/**
 * 일지 달력
 * ---------------------------------------------------------------------------
 * 법정 일지는 빠진 날이 곧 지적 사항이다. 한 달을 한눈에 보고 빠진 날을
 * 찾게 하는 것이 이 달력의 일이다.
 *
 * 한 칸에 담는 것
 *   날짜 숫자   일요일·공휴일은 빨강, 토요일은 파랑 — 종이 달력과 같다
 *   공휴일 이름 칸 위쪽에 작게
 *   작성한 일지 유형별 아이콘. 확인완료는 채운 모양, 작성완료는 테두리만
 *   미작성     점선 테두리의 유형 이름. 눈에 띄되 붉게 칠하지 않는다 —
 *              한 달에 스무 칸이 빨갛면 그때부터 아무도 보지 않는다
 *
 * 다른 달의 날짜는 흐리게 두고 표시하지 않는다. 받아 온 자료가 이번 달뿐이다.
 */

import { Calendar, Tooltip, Typography } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import type { CalendarDay, CalendarMonth } from '@eco/shared';
import { LOG_STATUS_LABELS, LOG_TYPE_SHORT } from '@eco/shared';
import { LOG_TYPE_COLOR, LOG_TYPE_ICON } from '../../components/logTypeVisual';

const { Text } = Typography;

export function LogCalendar({
  value,
  data,
  onMonthChange,
  onDayClick,
}: {
  value: Dayjs;
  data: CalendarMonth | undefined;
  onMonthChange: (month: Dayjs) => void;
  onDayClick: (day: CalendarDay) => void;
}) {
  const byDate = new Map((data?.days ?? []).map((d) => [d.date, d]));
  const today = dayjs().format('YYYY-MM-DD');

  return (
    <Calendar
      value={value}
      onPanelChange={(date) => onMonthChange(date)}
      onSelect={(date, info) => {
        // 머리의 연·월 선택도 onSelect 를 부른다. 날짜를 누른 경우만 연다.
        if (info.source !== 'date') {
          onMonthChange(date);
          return;
        }
        const day = byDate.get(date.format('YYYY-MM-DD'));
        if (day) onDayClick(day);
        else onMonthChange(date); // 다른 달의 날짜를 누르면 그 달로 넘어간다
      }}
      fullCellRender={(current, info) => {
        if (info.type !== 'date') return info.originNode;

        const date = current.format('YYYY-MM-DD');
        const day = byDate.get(date);
        const inMonth = current.month() === value.month();
        const dow = current.day();
        const holiday = day?.holidays.length ? day.holidays : null;

        const numberColor = !inMonth
          ? '#bfbfbf'
          : holiday || dow === 0
            ? '#cf1322'
            : dow === 6
              ? '#1d39c4'
              : undefined;

        return (
          <div
            className="eco-cal-cell"
            style={{
              margin: '0 2px',
              padding: '4px 6px',
              minHeight: 92,
              borderTop: `2px solid ${date === today ? '#0f7b6c' : '#f0f0f0'}`,
              background: date === today ? '#f6ffed' : undefined,
              opacity: inMonth ? 1 : 0.45,
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}>
              <Text
                ellipsis
                style={{ fontSize: 11, color: '#cf1322', maxWidth: '75%' }}
                title={holiday?.map((h) => h.name).join(', ')}
              >
                {inMonth && holiday ? holiday.map((h) => h.name).join(' · ') : ''}
              </Text>
              <span style={{ fontWeight: 600, color: numberColor }}>{current.date()}</span>
            </div>

            {inMonth && day && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                {day.logs.map((log) => (
                  <Tooltip
                    key={log.dailyLogId}
                    title={`${LOG_TYPE_SHORT[log.logType]} · ${LOG_STATUS_LABELS[log.status]}`}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        padding: '1px 6px',
                        borderRadius: 10,
                        fontSize: 11,
                        // 확인완료는 채우고, 작성완료는 테두리만. 결재가 끝났는지
                        // 멀리서도 구분된다.
                        color: log.status === 'CONFIRMED' ? '#fff' : LOG_TYPE_COLOR[log.logType],
                        background:
                          log.status === 'CONFIRMED' ? LOG_TYPE_COLOR[log.logType] : '#fff',
                        border: `1px solid ${LOG_TYPE_COLOR[log.logType]}`,
                      }}
                    >
                      {LOG_TYPE_ICON[log.logType]}
                      {log.status === 'CONFIRMED' && <CheckCircleFilled style={{ fontSize: 10 }} />}
                    </span>
                  </Tooltip>
                ))}

                {day.missing.map((type) => (
                  <Tooltip key={type} title={`${LOG_TYPE_SHORT[type]} 미작성 — 눌러서 작성`}>
                    <span
                      style={{
                        padding: '0 6px',
                        borderRadius: 10,
                        fontSize: 11,
                        color: '#8c8c8c',
                        border: '1px dashed #d9d9d9',
                        background: '#fafafa',
                      }}
                    >
                      {LOG_TYPE_SHORT[type]}
                    </span>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        );
      }}
    />
  );
}
